/**
 * Element: swatch-popover-content 
 * Purpose:
 * - Defines the events needed to swap selected swatch
 * - Defines the events needed to add the samples to the cart
 * - Defines upsell Add To Cart Events
*/
if ( !customElements.get('swatch-popover-content') ) {
  class SwatchPopoverContent extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.swatchOrderBtn = this.querySelector('.swatch-popover__order-btn');
      this.upsellBtns = this.querySelectorAll('#upsell-btn-atc');

      this.bindEvents();
    }

    bindEvents() {
      if (this.swatchOrderBtn) {
        this.swatchOrderBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }

      if (this.upsellBtns.length > 0) {
        this.upsellBtns.forEach( (btn) => {
          btn.addEventListener( 'click', this.handleAddToCart.bind(this) );
        });
      }
    }

    handleAddToCart( event ) {
      const button = event.currentTarget;
      const { type } = button.dataset;

      if ( type === 'swatch' ) {
        const variantId = button.getAttribute('data-swatch-product');
        const swatchHandle = button.getAttribute('data-selected-swatch');
        this.swatchPopoverAddToCart(variantId, swatchHandle);
        return;
      }

      if ( type === 'upsell' ) {
        const variantId = button.getAttribute('data-variant-id');
        this.swatchPopoverAddToCart(variantId);
        return;
      }
    }

    swatchPopoverAddToCart( variantId, swatchHandle = null ) {
      if (!variantId) return;

      // Prepare form data
      const formData = new FormData();
      formData.append( 'id', variantId );
      formData.append( 'quantity', '1' );
      
      const selectedOption = this.querySelector('.swatch-input:checked');
      if ( selectedOption != null ) {
        const propKey = selectedOption.getAttribute( "data-option" );
        const propValue = selectedOption.getAttribute( "value" );
        formData.append( `properties[${propKey}]`, propValue );
      }
      
      if (swatchHandle) {
        formData.append('properties[_swatch-handle]', swatchHandle);
      }

      // Add sections for cart updates
      if ( this.cartDrawer && this.cartDrawer.getSectionsToRender ) {
        formData.append(
          'sections',
          this.cartDrawer.getSectionsToRender().map((section) => section.section || section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }

      // Fetch config
      const config = theme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('Error adding to cart:', response.message);
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'swatch-popover',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
        } else {
          // Success - publish cart update event
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'swatch-popover',
            productVariantId: variantId,
            cartData: response,
          });

          // Close the popover
          const popover = this.closest( 'swatch-popover' );
          if ( popover != null ) {
            popover.close();
          }

          // Open the cart drawer
          if (this.cartDrawer?.open) {
            this.cartDrawer.open();
          }
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
      });
    }
  }

  customElements.define( 'swatch-popover-content' , SwatchPopoverContent );
} 

/**
 * Element: swatch-popover
 * Purpose:
 * - Defines the Modal element for the samples content that is triggered by the samples-toggle element
 * - Manages the events related to the modal 
*/
if ( !customElements.get('swatch-popover') ) {
  class SwatchPopover extends HTMLElement {
    constructor() {
      super();

      this.drawer = this.querySelector('.swatch-popover');
      this.closeBtn = this.drawer.querySelector('.swatch-popover__close');
      this.popoverOverlay = this.nextElementSibling;
    }

    connectedCallback() {
      // console.log( '[Swatch Popover] Added Element to the page.' );
      this.bindEvents();
    }

    bindEvents() {
      if ( this.closeBtn != null ) {
        this.closeBtn.addEventListener( 'click', this.close.bind(this) );
      }

      if ( this.popoverOverlay != null ) {
        this.popoverOverlay.addEventListener( 'click', this.close.bind(this) );
      }
    }

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.popoverOverlay.classList.add('open');
      
      setTimeout(() => {
        document.body.classList.add('overflow-hidden');
      }, 200);
      document.body.setAttribute( "data-lenis-prevent", "true" );

      // header and announcement bar z-index adjustments
      document.querySelector('.announcement-bar-section').style.zIndex = '1';
      document.querySelector('.header-section').style.zIndex = '1';
    }

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.popoverOverlay.classList.remove('open');
      
      // Remove overlay drawer
      const overlayDrawer = document.querySelector('.overlay-drawer');
      if ( overlayDrawer ) {
        overlayDrawer.classList.remove('open');
      }
      
      document.body.classList.remove('overflow-hidden');
      document.body.removeAttribute("data-lenis-prevent");

      // header and announcement bar z-index adjustments
      setTimeout(() => {
        document.querySelector('.announcement-bar-section').style.zIndex = '3';
        document.querySelector('.header-section').style.zIndex = '3';
      }, 200);
    }

    checkOutside() {
      document.body.addEventListener('click', (e) => {
        if (!this.drawer) return;
        this.drawerOutside = !e.target.closest('.swatch-popover');
        this.openBtnOutside = !e.target.closest('.btn-view-colors');
        if (this.drawerOutside && this.openBtnOutside) {
          this.close();
        }
      });
    }
  }

  customElements.define( 'swatch-popover', SwatchPopover );
}

/**
 * Element: samples-toggle
 * Purpose: 
 * - Defines a custom element that will hold buttons that can trigger a sample modal open.
 * - Stores the core settings / template of the modal as JSON.
 */
if ( !customElements.get('samples-toggle') ) {
  class SamplesToggle extends HTMLElement {
    constructor() {
      super();

      this.toggles = this.querySelectorAll('[data-toggle]');
      this.samplesSettings = null;
    }

    connectedCallback() {
      // console.log( '[Samples Toggle] Added Element to the page.' );
      this.initSampleSettings();
      this.bindEvents();
    }

    initSampleSettings() {
      try {
        const scriptElement = this.querySelector("script.samples-settings");
        if ( scriptElement != null ) {
          const rawJSON = scriptElement.innerHTML;
          this.samplesSettings = JSON.parse( rawJSON );
        }
      } catch ( error ) {
        console.error( error );
      }
    }

    bindEvents() {
      for ( let i = 0; i < this.toggles.length; i++ ) {
        const toggle = this.toggles[i];
        toggle.addEventListener( "click" , this.toggleSwatchPopover.bind( this ) );
      }
    }

    toggleSwatchPopover( e ) {
      const buttonClicked = e.currentTarget;
      const optionSelected = buttonClicked.getAttribute( "data-trigger-source" );

      const productWrapper = buttonClicked.closest( ".product-info-inner" );
      if ( productWrapper != null ) {
        const optionInputSelected = productWrapper.querySelector( `custom-product-options .option-${optionSelected} input:checked` );
        const entryToSelect = optionInputSelected.getAttribute('data-entry-id');

        const swatchPopover = productWrapper.querySelector( 'swatch-popover' );
        if ( swatchPopover != null ) {
          const templateToLoad = this.samplesSettings.template;
          const temp = document.createElement( 'div' );
          temp.innerHTML = templateToLoad;

          const swatchPopoverInjectPoint = swatchPopover.querySelector( '.content' );
          if ( swatchPopoverInjectPoint != null ) {
            swatchPopoverInjectPoint.innerHTML = "";
            swatchPopoverInjectPoint.innerHTML = temp.innerHTML;
          }

          const selectedOptionInput = swatchPopover.querySelector( `.content .swatch-input[data-entry-id="${entryToSelect}"]` );
          if ( selectedOptionInput != null ) {
            selectedOptionInput.checked = true;

            // scroll to the visible label that has width / height
            selectedOptionInput.nextElementSibling.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }

          swatchPopover.open();
        }
      }
    }
  }

  customElements.define( 'samples-toggle', SamplesToggle );
}



// class SwatchPopover extends HTMLElement {
//   constructor() {
//     super();

//     this.productInfo = document.querySelector('.product-info-main');
//     this.btn_view = this.productInfo.querySelectorAll('.btn-view-colors');
//     this.drawer = this.querySelector('.swatch-popover');
//     this.closeBtn = this.drawer.querySelector('.swatch-popover__close');
//     this.colorsWrapper = this.drawer.querySelector('.color__wrapper--query');
//     this.addToCartbtn = document.getElementById('atc-button');
//     this.swatchOrderBtn = this.drawer.querySelector('.swatch-popover__order-btn');
//     this.upsellBtns = this.drawer.querySelectorAll('#upsell-btn-atc');
//     this.cartDrawer = document.querySelector('.cart-drawer');
//     this.popoverOverlay = document.querySelector('.swatch-popover__overlay');

//     this.checkOutside();
//     this.bindEvents();
//   }

//   bindEvents() {
//     for ( const foo of this.btn_view ) {
//       foo.addEventListener('click', this.open.bind(this));
//     }
      

//     if (this.closeBtn) {
//       this.closeBtn.addEventListener('click', this.close.bind(this));
//     }
    
//     if (this.swatchOrderBtn) {
//       this.swatchOrderBtn.addEventListener('click', this.handleAddToCart.bind(this));
//     }

//     if (this.upsellBtns.length > 0) {
//       this.upsellBtns.forEach((btn) => {
//         btn.addEventListener('click', this.handleAddToCart.bind(this));
//       });
//     }
//   }

//   open() {
//     if (!this.drawer) return;
//     this.drawer.classList.add('is-open');
//     this.popoverOverlay.classList.add('open');
    
//     setTimeout(() => {
//       document.body.classList.add('overflow-hidden');
//     }, 200);
//     document.body.setAttribute("data-lenis-prevent", "true");

//     // header and announcement bar z-index adjustments
//     document.querySelector('.announcement-bar-section').style.zIndex = '1';
//     document.querySelector('.header-section').style.zIndex = '1';
//   }

//   close() {
//     this.drawer.classList.remove('is-open');
//     this.popoverOverlay.classList.remove('open');
    
//     // Remove overlay drawer
//     const overlayDrawer = document.querySelector('.overlay-drawer');
//     if (overlayDrawer) {
//       overlayDrawer.classList.remove('open');
//     }
    
//     document.body.classList.remove('overflow-hidden');
//     document.body.removeAttribute("data-lenis-prevent");

//     // header and announcement bar z-index adjustments
//     setTimeout(() => {
//       document.querySelector('.announcement-bar-section').style.zIndex = '3';
//       document.querySelector('.header-section').style.zIndex = '3';
//     }, 200);
//   }

//   checkOutside() {
//     document.body.addEventListener('click', (e) => {
//       if (!this.drawer) return;
//       this.drawerOutside = !e.target.closest('.swatch-popover');
//       this.openBtnOutside = !e.target.closest('.btn-view-colors');
//       if (this.drawerOutside && this.openBtnOutside) {
//         this.close();
//       }
//     });
//   }

//   handleAddToCart(event) {
//     const button = event.currentTarget;
//     const { type } = button.dataset;

//     if (type === 'swatch') {
//       const variantId = button.getAttribute('data-swatch-product');
//       const swatchHandle = button.getAttribute('data-selected-swatch');
//       this.swatchPopoverAddToCart(variantId, swatchHandle);
//       return;
//     }
//     // Upsells
//     if (type === 'upsell') {
//       const variantId = button.getAttribute('data-variant-id');
//       this.swatchPopoverAddToCart(variantId);
//       return;
//     }
//   }

//   swatchPopoverAddToCart(variantId, swatchHandle = null) {
//     if (!variantId) return;

//     // Prepare form data
//     const formData = new FormData();
//     formData.append('id', variantId);
//     formData.append('quantity', '1');

//     // Add swatch handle as a line property
//     if (swatchHandle) {
//       formData.append('properties[_swatch-handle]', swatchHandle);
//     }

//     // Add sections for cart updates
//     if (this.cartDrawer && this.cartDrawer.getSectionsToRender) {
//       formData.append(
//         'sections',
//         this.cartDrawer.getSectionsToRender().map((section) => section.section || section.id)
//       );
//       formData.append('sections_url', window.location.pathname);
//     }

//     // Fetch config
//     const config = theme.utils.fetchConfig('javascript');
//     config.headers['X-Requested-With'] = 'XMLHttpRequest';
//     delete config.headers['Content-Type'];
//     config.body = formData;

//     fetch(`${routes.cart_add_url}`, config)
//       .then((response) => response.json())
//       .then((response) => {
//         if (response.status) {
//           console.error('Error adding to cart:', response.message);
//           theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
//             source: 'swatch-popover',
//             productVariantId: variantId,
//             errors: response.errors || response.description,
//             message: response.message,
//           });
//         } else {
//           // Success - publish cart update event
//           theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
//             source: 'swatch-popover',
//             productVariantId: variantId,
//             cartData: response,
//           });

//           // Close the popover
//           this.close();

//           // Open the cart drawer
//           if (this.cartDrawer?.open) {
//             this.cartDrawer.open();
//           }
//         }
//       })
//       .catch((error) => {
//         console.error('Error adding to cart:', error);
//       });
//   }
// }

// customElements.define('swatch-popover', SwatchPopover);